// src/components/facts/RandomFactCard.tsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { AppDispatch, RootState } from '../../store';
import { fetchRandomFact } from '../../store/slices/factsSlice';
import FactCard from './FactCard';
import { Fact } from '../../types';

const LOCAL_STORAGE_KEY = 'daily_fact';

const RandomFactCard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { dailyFact, loading, error } = useSelector(
    (state: RootState) => state.facts
  );
  const userId = useSelector((state: RootState) => state.auth.user?.id);
  const [localFact, setLocalFact] = useState<Fact | null>(null);

  useEffect(() => {
    const loadFact = async () => {
      // 嘗試從 localStorage 獲取已保存的冷知識
      const savedFactJson = localStorage.getItem(LOCAL_STORAGE_KEY);

      if (savedFactJson) {
        try {
          // 嘗試解析保存的 JSON
          const savedFact = JSON.parse(savedFactJson);

          // 檢查日期是否是今天，並且是否已過了早上5點
          const currentDate = new Date();

          // 獲取今天凌晨5點的時間戳
          const today5am = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate(),
            5,
            0,
            0
          ).getTime();

          // 判斷是否需要更新
          // 1. 如果現在時間早於今天5點，且保存時間是昨天5點之後，則不更新
          // 2. 如果現在時間晚於今天5點，且保存時間是今天5點之後，則不更新
          // 3. 其他情況都需要更新
          const currentTime = currentDate.getTime();

          if (
            (currentTime < today5am &&
              savedFact.savedTime >= today5am - 24 * 60 * 60 * 1000) ||
            (currentTime >= today5am && savedFact.savedTime >= today5am)
          ) {
            // 不需要更新，使用保存的冷知識
            setLocalFact(savedFact.fact);
            return;
          }
        } catch (e) {
          console.error('解析保存的冷知識失敗:', e);
          // 解析失敗，從 API 獲取新的冷知識
        }
      }

      // 沒有保存的冷知識或需要更新，從 API 獲取新的
      dispatch(fetchRandomFact(userId));
    };

    loadFact();
  }, [dispatch, userId]);

  // 當從 API 獲取到新的冷知識時，保存到 localStorage
  useEffect(() => {
    if (dailyFact && !localFact) {
      const factToSave = {
        fact: dailyFact,
        savedTime: new Date().getTime(),
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(factToSave));
      setLocalFact(dailyFact);
    }
  }, [dailyFact, localFact]);

  // 刷新獲取新的隨機冷知識
  const handleRefresh = () => {
    // 清除本地保存的冷知識
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    setLocalFact(null);

    // 從 API 獲取新的冷知識
    dispatch(fetchRandomFact(userId));
  };

  // 顯示的冷知識 (優先使用本地保存的)
  const displayFact = localFact || dailyFact;

  if (loading && !displayFact) {
    return (
      <Paper className="p-6 text-center">
        <CircularProgress />
        <Typography variant="body1" className="mt-2">
          正在獲取隨機冷知識...
        </Typography>
      </Paper>
    );
  }

  if (error && !displayFact) {
    return (
      <Paper className="p-6 text-center">
        <Typography variant="body1" color="error">
          {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRefresh}
          className="mt-3"
        >
          重試
        </Button>
      </Paper>
    );
  }

  return (
    <Box>
      <Box className="flex justify-between items-center mb-4">
        <Typography variant="h5" component="h2" className="font-bold">
          每日冷知識
        </Typography>
        <Button startIcon={<Refresh />} onClick={handleRefresh} variant="text">
          換一則
        </Button>
      </Box>

      {displayFact ? (
        <FactCard fact={displayFact} variant="highlighted" />
      ) : (
        <Paper className="p-6 text-center">
          <Typography variant="body1">暫無冷知識，請稍後再試。</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RandomFactCard;
